/**
 * User management API types
 */

import type { Role, Status } from './identity-contracts';
import type {
    PermissionsGrant,
    RoleAssignmentDetailed,
    RoleAssignmentPermissionContext,
    RoleAssignmentPermissionsView,
} from './permission.types';
import type { RoleAssignmentCustomConfig } from './backend_config.types';

/**
 * Unified request for provisioning users.
 * organization_id is required for OUser, optional/null for IUser
 */
export interface ProvisionUserRequest {
    primary_email: string;
    emails: string[];
    role: Role;
    organization_id: number | null; // Required for organizational roles, null/undefined for internal users
    first_name: string | null;
    last_name: string | null;
    skip_verification?: boolean;
    policies?: string[];
}

/**
 * Response from provisioning a user.
 * Contains the created user, invitation token, and expiration.
 */
export interface ProvisionUserResponse {
    user: UserProfileDetailedResponse | null;
    invitation_token: string | null;
    invitation_expires_at: string | null; // ISO date string
    primary_email: string;
}

export interface ProvisionInternalUserCreatedUser {
    user_uuid: string;
}

export interface ProvisionInternalUserResponse {
    user: ProvisionInternalUserCreatedUser | null;
    invitation_token: string | null;
    invitation_expires_at: string | null; // ISO date string
    primary_email: string;
}

export interface ProvisionOrganizationalUserRequest {
    primary_email: string;
    emails: string[];
    role: Role;
    organization_uuid: string;
    first_name: string | null;
    last_name: string | null;
    skip_verification?: boolean;
    policies?: string[];
}

export interface ProvisionOrganizationalUserCreatedUser {
    user_uuid: string;
}

export interface ProvisionOrganizationalUserResponse {
    user: ProvisionOrganizationalUserCreatedUser | null;
    invitation_token: string | null;
    invitation_expires_at: string | null; // ISO date string
    primary_email: string;
}

/**
 * Request to update a user
 */
export interface UpdateUserRequest {
    primary_email?: string;
    status?: Status;
    first_name?: string | null;
    last_name?: string | null;
}

export interface UpdateUserResponse {
    user_uuid: string;
    first_name: string | null;
    last_name: string | null;
    primary_email: string;
    emails: string[];
    phones?: string[];
    primary_phone?: string | null;
    totp_enabled: boolean;
    status: Status;
    invitation_token?: string;
}

/**
 * User profile response with full details
 */
export interface UserProfileResponse {
    user_id: number;
    user_uuid?: string;
    first_name: string | null;
    last_name: string | null;
    primary_email: string;
    emails: string[];
    phones?: string[];
    primary_phone?: string | null;
    totp_enabled: boolean;
    status: Status;
    invitation_token?: string;
}
export interface UserProfileDetailedResponse extends UserProfileResponse {
    default_role_assignment: RoleAssignmentDetailed | null;
    other_role_assignments: RoleAssignmentDetailed[];
}

export interface UserProfilePublicResponse {
    user_uuid: string;
    first_name: string | null;
    last_name: string | null;
    primary_email: string;
    emails: string[];
    phones?: string[];
    primary_phone?: string | null;
    totp_enabled: boolean;
    mfa_required: boolean;
    mfa_email_allowed: boolean;
    mfa_sms_allowed: boolean;
    status: Status;
    invitation_token?: string;
}

export interface RoleAssignmentsContainer<
    RoleAssignmentType extends RoleAssignmentPermissionContext = RoleAssignmentDetailed,
> {
    default_role_assignment: RoleAssignmentType | null;
    other_role_assignments: RoleAssignmentType[];
}

export interface UserProfileDetailedPublicResponse extends UserProfilePublicResponse {
    default_role_assignment: RoleAssignmentPermissionsView | null;
    other_role_assignments: RoleAssignmentPermissionsView[];
}

export function getAllRoleAssignments<
    RoleAssignmentType extends RoleAssignmentPermissionContext>(user: RoleAssignmentsContainer<RoleAssignmentType>): RoleAssignmentType[] {
    return [
        ...(user.default_role_assignment ? [user.default_role_assignment] : []),
        ...user.other_role_assignments,
    ];
}

/**
 * Request to assign a role to a user
 */
export interface AssignRoleRequest {
    user_uuids: string[];
    role: Role;
    organization_uuid: string | null; // Required for organizational roles, null for global roles
    permissions?: PermissionsGrant[]; // Optional permissions to grant immediately after role assignment creation
}

/**
 * Request to revoke a role from a user
 */
export interface RevokeRoleRequest {
    role_assignment_uuid: string;
    user_uuid: string; // Cross-check for safety
}

/**
 * Request to update a role assignment
 */
export interface UpdateRoleAssignmentRequest {
    role_assignment_uuid: string;
    user_uuid: string; // Cross-check for safety
    role?: Role;
    profile_name?: string; // Deprecated - not supported in current schema
    user_config?: RoleAssignmentCustomConfig;
}

export type UpdateRoleAssignmentBodyRequest = Omit<
    UpdateRoleAssignmentRequest,
    'role_assignment_uuid'
>;

/**
 * Request to delete own account (self-delete)
 */
export interface DeleteMyAccountRequest {
    password: string;
}

/**
 * Response from deleting own account
 */
export interface DeleteMyAccountResponse {
    success: boolean;
    message: string;
}

/**
 * Get profile response (simplified user profile)
 */
export interface GetProfileResponse {
    user: {
        user_uuid: string;
        primary_email: string;
        emails: string[];
        status: Status;
        totp_enabled: boolean;
    } | null;
}

/**
 * Request to update user profile
 */
export interface UpdateProfileRequest {
    primary_email: string;
}

/**
 * Request to reset password (admin operation)
 */
export interface ResetPasswordAdminRequest {
    new_password: string;
}

/**
 * Request to send a password reset email (admin operation)
 */
export interface RequestPasswordResetAdminRequest {
    email: string;
}

/**
 * Request to set the MFA requirement policy for a user.
 */
export interface SetUserMfaRequiredRequest {
    required: boolean;
}

/**
 * Set user emails request (full end state)
 */
export interface SetUserEmailsRequest {
    emails: string[];
    primary_email: string;
}

/**
 * Set user phone numbers request (full end state)
 */
export interface SetUserPhonesRequest {
    phones: string[];
    primary_phone: string | null;
}

export interface RequestAddEmailVerificationRequest {
    email: string;
    skip_verification?: boolean;
}

export interface RequestAddEmailVerificationResponse {
    message: string;
    verification_link?: string;
}

export interface RequestAddPhoneVerificationRequest {
    phone: string;
    skip_verification?: boolean;
}

export interface RequestAddPhoneVerificationResponse {
    message: string;
    delivery_destination?: string;
    expires_at?: string;
}

export interface VerifyAddPhoneRequest {
    phone: string;
    code: string;
}

/**
 * Get emails response
 */
export interface GetEmailsResponse {
    primary_email: string;
    emails: Array<{
        email: string;
        is_primary: boolean;
        is_verified: boolean;
    }>;
}

/**
 * Get my roles response
 */
export interface GetMyRolesResponse {
    roles: Array<{
        role: Role;
        organization_uuid: string | null;
        organization_name?: string;
    }>;
}
